import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router';
import {Alert} from '@interstate/components/Alert';
import {useAuth} from '../context/AuthContext';
import {api, ApiError} from '../lib/api';
import {PortfolioDashboard, type PortfolioData, type ViewId} from '../components/dashboard';
import {DashboardHeader} from '../components/DashboardHeader';
import {DashboardSidebar} from '../components/DashboardSidebar';
import {LoadingState, EmptyState, ErrorState, InboxIcon} from '../components/States';
import {formatDate} from '../lib/time';
import {ALL, headingFor, portfolioOptions, resolveSelection} from '../lib/portfolio-nav';

/**
 * The dashboard renders the caller's SCOPED data (project ADR-0006): the server returns only the
 * domains they may see (viewer→portfolio, owner→domain, admin→all) plus the portfolios they are
 * entitled to. Those entitlements become the header's segmented switcher, and the selection narrows
 * the already-scoped set client-side — so the switcher can only ever move between portfolios the
 * server has already cleared. A leader's scope is always exactly one portfolio; only an administrator
 * gets the aggregate "All" option, since cross-portfolio roll-up is an oversight view rather than a
 * leader's. A caller with no portfolio entitlement (a domain-owner) gets no switcher and sees their domains
 * unfiltered. Version history is available to everyone — the list is timestamps only and each
 * version's content is scoped server-side per request.
 */
export default function Dashboard(): React.ReactElement {
  const {user, logout} = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.roles.includes('admin') ?? false;

  const [data, setData] = useState<PortfolioData | null>(null);
  const [versions, setVersions] = useState<{uploadedAt: string; uploadedBy: string}[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<string>('');
  const [selection, setSelection] = useState<string>(ALL);
  const [activeView, setActiveView] = useState<ViewId>('portfolio');
  const [state, setState] = useState<'loading' | 'ready' | 'awaiting' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const retry = () => setReloadKey(k => k + 1);

  const latestVersion = versions[0]?.uploadedAt ?? '';
  const currentVersion = useMemo(
    () => versions.find(v => v.uploadedAt === selectedVersion) ?? versions[0],
    [versions, selectedVersion]
  );
  const isViewingLatest = !selectedVersion || selectedVersion === latestVersion;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setState('loading');
      try {
        // Everyone can browse the history of the domains they can see — the list is timestamps only,
        // and each version's content is scoped server-side to the caller.
        const {versions: vs} = await api.listSnapshots().catch(() => ({versions: []}));
        if (!cancelled) setVersions(vs);
        if (!cancelled && vs.length > 0) setSelectedVersion(vs[0].uploadedAt);
        const portfolio = (await api.getDashboard()) as PortfolioData;
        if (cancelled) return;
        setData(portfolio);
        setState(portfolio.domains.length === 0 ? 'awaiting' : 'ready');
      } catch (err) {
        if (cancelled) return;
        setState('error');
        setMessage(err instanceof ApiError ? err.message : 'Failed to load dashboard.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const onSelectVersion = async (uploadedAt: string) => {
    setSelectedVersion(uploadedAt);
    setMenuOpen(false);
    setState('loading');
    try {
      const portfolio = (await api.getDashboard(uploadedAt)) as PortfolioData;
      setData(portfolio);
      setState(portfolio.domains.length === 0 ? 'awaiting' : 'ready');
    } catch (err) {
      setState('error');
      setMessage(err instanceof ApiError ? err.message : 'Failed to load version.');
    }
  };

  // The switcher's options, derived from the caller's entitlements as reported by the server — every
  // portfolio they can reach gets a pill, including ones holding no domains yet.
  const options = useMemo(
    () => portfolioOptions(data?.portfolios ?? [], data?.domains ?? [], isAdmin),
    [data, isAdmin]
  );

  // Keep the selection valid whenever the options change (data reload, version switch, or a domain's
  // portfolio reassignment) — falls back to a real portfolio instead of rendering an empty dashboard.
  useEffect(() => {
    setSelection(current => resolveSelection(current, options));
  }, [options]);

  // Apply the selection to the already-scoped data; `DashboardProvider` then narrows service areas
  // to this filtered set automatically (context.tsx).
  const filtered = useMemo<PortfolioData | null>(() => {
    if (!data) return null;
    if (selection === ALL) return data;
    return {...data, domains: data.domains.filter(d => d.portfolio === selection)};
  }, [data, selection]);

  // The page background is #F8FAFC rather than Tailwind's bg-gray-50: PortfolioDashboard paints that
  // exact value, and the two greys differ just enough to leave a visible seam where the content ends.
  return (
    <div style={{minHeight: '100vh', background: '#F8FAFC'}}>
      <DashboardHeader
        activeView={activeView}
        onSelectView={setActiveView}
        options={options}
        selection={selection}
        onSelectPortfolio={setSelection}
        onOpenMenu={() => setMenuOpen(true)}
      />

      <main>
        {state === 'loading' && <LoadingState label="Loading dashboard…" />}
        {state === 'awaiting' && (
          <EmptyState
            icon={<InboxIcon />}
            title={isAdmin ? 'No portfolio uploaded yet' : 'Nothing in your scope yet'}
            message={
              isAdmin
                ? 'No portfolio has been uploaded. Open the console to upload one and classify its domains.'
                : 'No domains are visible to your account yet. Once an admin assigns you a portfolio or domain, it will appear here.'
            }
            action={isAdmin ? {label: 'Go to console', onClick: () => navigate('/manage')} : undefined}
          />
        )}
        {state === 'error' && <ErrorState message={message} onRetry={retry} />}
        {state === 'ready' && filtered && (
          <>
            {!isViewingLatest && currentVersion && (
              <div className="max-w-[1400px] mx-auto px-12 pt-10">
                <Alert type="info">
                  Viewing the {formatDate(currentVersion.uploadedAt)} version.{' '}
                  <button className="underline font-semibold" onClick={() => void onSelectVersion(latestVersion)}>
                    Return to latest →
                  </button>
                </Alert>
              </div>
            )}
            {filtered.domains.length === 0 ? (
              // An entitled portfolio that holds no domains yet: the switcher stays put and the
              // content renders empty, so the portfolio is never hidden from someone who can see it.
              <EmptyState
                icon={<InboxIcon />}
                title={`No domains in ${headingFor(selection)}`}
                message={
                  isAdmin
                    ? 'This portfolio has no domains assigned yet. Open the console to classify domains into it.'
                    : 'This portfolio has no domains assigned yet.'
                }
                action={isAdmin ? {label: 'Go to console', onClick: () => navigate('/manage')} : undefined}
              />
            ) : (
              <div key={selection}>
                <PortfolioDashboard data={filtered} activeView={activeView} />
              </div>
            )}
          </>
        )}
      </main>

      <DashboardSidebar
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        isAdmin={isAdmin}
        versions={versions}
        selectedVersion={selectedVersion || latestVersion}
        onSelectVersion={at => void onSelectVersion(at)}
        onManage={() => navigate('/manage')}
        onSignOut={() => void logout()}
      />
    </div>
  );
}
